import { AxisPath, DimensionValue, ReportGridCellContext, ReportGridDataset, ReportGridDimension } from '../types';
import { parseDecimal, serializeDecimal } from './decimal';

export interface AxisNode { id: string; path: AxisPath; children: AxisNode[]; depth: number }
export interface ReportGridModel {
  balance?: { initialValue: string; label?: string; getClosing: (column: AxisNode) => string | null };
  rows: AxisNode; columns: AxisNode;
  rowNodes: AxisNode[]; columnNodes: AxisNode[];
  getValue: (row: AxisNode, column: AxisNode) => string | null;
  context: (row: AxisNode, column: AxisNode) => ReportGridCellContext;
}
// JSON preserves boundaries, value types and special characters; no delimiter collisions.
export const pathId = (path: AxisPath): string => JSON.stringify(path);
function validateAxis(entries: ReportGridDataset['index'], dimensions: readonly ReportGridDimension[], axis: string): AxisPath[] {
  if (!Array.isArray(entries) || !Array.isArray(dimensions) || !dimensions.length) throw new Error(`${axis}: dimensões obrigatórias.`);
  const keys = new Set<string>();
  dimensions.forEach(d => {
    if (!d || typeof d.key !== 'string' || !d.key || typeof d.label !== 'string' || keys.has(d.key)) throw new Error(`${axis}: chave de dimensão inválida ou duplicada.`);
    keys.add(d.key);
  });
  const seen = new Set<string>();
  return entries.map((entry, i) => {
    const path: AxisPath = Array.isArray(entry) ? [...entry] : [entry as DimensionValue];
    if (path.length !== dimensions.length) throw new Error(`${axis}[${i}]: profundidade incompatível.`);
    path.forEach(v => {
      if (!(v === null || typeof v === 'string' || typeof v === 'boolean' || (typeof v === 'number' && Number.isFinite(v)))) throw new Error(`${axis}[${i}]: chave inválida.`);
    });
    const id = pathId(path);
    if (seen.has(id)) throw new Error(`${axis}[${i}]: caminho duplicado.`);
    seen.add(id);
    return path;
  });
}
function buildTree(paths: AxisPath[]) {
  const root: AxisNode = { id: pathId([]), path: [], depth: 0, children: [] };
  const nodes = new Map<string, AxisNode>([[root.id, root]]);
  const ancestors = paths.map(path => {
    const chain = [root];
    let parent = root;
    path.forEach((_, i) => {
      const prefix = path.slice(0, i + 1);
      const id = pathId(prefix);
      let node = nodes.get(id);
      if (!node) {
        node = { id, path: prefix, depth: prefix.length, children: [] };
        nodes.set(id, node);
        parent.children.push(node);
      }
      chain.push(node);
      parent = node;
    });
    return chain;
  });
  return { root, nodes: [...nodes.values()].filter(n => n.depth > 0), ancestors };
}
export function buildReportGridModel(dataset: ReportGridDataset): ReportGridModel {
  if (!dataset || typeof dataset !== 'object') throw new Error('Dataset obrigatório.');
  const measure = dataset.measure;
  if (!measure || measure.aggregation !== 'sum' || typeof measure.key !== 'string' || typeof measure.label !== 'string' ||
    !Number.isInteger(measure.decimalPlaces) || measure.decimalPlaces < 0 || measure.decimalPlaces > 20) throw new Error('Medida inválida: use soma e escala de 0 a 20.');
  if (measure.currency !== undefined && (typeof measure.currency !== 'string' || !/^[a-z]{3}$/i.test(measure.currency))) throw new Error('Código de moeda inválido.');
  const rowPaths = validateAxis(dataset.index, dataset.rowDimensions, 'index');
  const columnPaths = validateAxis(dataset.columns, dataset.columnDimensions, 'columns');
  if (!Array.isArray(dataset.data) || dataset.data.length !== rowPaths.length) throw new Error('data: quantidade de linhas incompatível.');
  const rows = buildTree(rowPaths), columns = buildTree(columnPaths);
  const sums = new Map<string, Map<string, bigint>>();
  dataset.data.forEach((cells, i) => {
    if (!Array.isArray(cells) || cells.length !== columnPaths.length) throw new Error(`data[${i}]: quantidade de colunas incompatível.`);
    cells.forEach((cell, j) => {
      if (cell === null) return;
      let value: bigint;
      try { value = parseDecimal(cell, measure.decimalPlaces); }
      catch (e) { throw new Error(`data[${i}][${j}]: ${(e as Error).message}`); }
      // Every source cell contributes once per ancestor pair; subtotal rows never become inputs.
      rows.ancestors[i].forEach(row => {
        let byColumn = sums.get(row.id);
        if (!byColumn) { byColumn = new Map(); sums.set(row.id, byColumn); }
        columns.ancestors[j].forEach(column => byColumn!.set(column.id, (byColumn!.get(column.id) ?? BigInt(0)) + value));
      });
    });
  });
  const getValue = (row: AxisNode, column: AxisNode) => {
    const value = sums.get(row.id)?.get(column.id);
    return value === undefined ? null : serializeDecimal(value, measure.decimalPlaces);
  };
  let balance: ReportGridModel['balance'];
  if (dataset.balance !== undefined) {
    if (!dataset.balance || dataset.balance.type !== 'opening-balance' || (dataset.balance.label !== undefined && typeof dataset.balance.label !== 'string')) throw new Error('Saldo inicial: configuração inválida.');
    let running: bigint;
    try { running = parseDecimal(dataset.balance.initialValue, measure.decimalPlaces); }
    catch (e) { throw new Error(`Saldo inicial: ${(e as Error).message}`); }
    const initialValue = serializeDecimal(running, measure.decimalPlaces);
    const closing = new Map<string, string>();
    const periodDimensions = dataset.balance.periodDimensions;
    if (periodDimensions !== undefined && (
      !Array.isArray(periodDimensions) || !periodDimensions.length ||
      periodDimensions.length > dataset.columnDimensions.length ||
      periodDimensions.some((key, index) => key !== dataset.columnDimensions[index].key)
    )) throw new Error('Saldo: as dimensões temporais devem iniciar o eixo de colunas, antes das categorias.');
    // Datasets antigos sem periodDimensions mantêm o saldo por coluna folha.
    const periodDepth = periodDimensions?.length ?? dataset.columnDimensions.length;
    let previousPeriod: string | undefined;
    // Column order is chronological by contract. Ancestors keep the LAST closing, never a sum.
    const lastPeriod = new Map<string, number>();
    columns.ancestors.forEach((chain, period) => {
      chain.forEach(node => {
        const last = lastPeriod.get(node.id);
        if (last !== undefined && last !== period - 1) throw new Error('Saldo: os períodos de cada grupo de colunas devem ser contíguos.');
        lastPeriod.set(node.id, period);
      });
      const periodNode = chain[periodDepth];
      if (periodNode.id === previousPeriod) return;
      previousPeriod = periodNode.id;
      // O subtotal temporal já contém todas as categorias do período.
      running += sums.get(rows.root.id)?.get(periodNode.id) ?? BigInt(0);
      const value = serializeDecimal(running, measure.decimalPlaces);
      chain.slice(0, periodDepth + 1).forEach(node => closing.set(node.id, value));
    });
    balance = { initialValue, label: dataset.balance.label, getClosing: column => closing.get(column.id) ?? null };
  }
  return {
    balance,
    rows: rows.root, columns: columns.root, rowNodes: rows.nodes, columnNodes: columns.nodes, getValue,
    context: (row, column) => ({
      rowPath: [...row.path], columnPath: [...column.path],
      rowFilters: row.path.map((value, i) => ({ field: dataset.rowDimensions[i].key, value })),
      columnFilters: column.path.map((value, i) => ({ field: dataset.columnDimensions[i].key, value })),
      measure: { ...measure }, value: getValue(row, column),
      isRowSubtotal: row.children.length > 0, isColumnSubtotal: column.children.length > 0,
      isGrandTotal: row.depth === 0 && column.depth === 0, metadata: dataset.metadata,
    }),
  };
}
/** Rows include parent subtotals. Columns use terminal visible groups plus expanded subtotals. */
export function visibleRows(root: AxisNode, expanded: ReadonlySet<string>): AxisNode[] {
  return root.children.flatMap(node => [node, ...(expanded.has(node.id) ? visibleRows(node, expanded) : [])]);
}
export function visibleColumns(root: AxisNode, expanded: ReadonlySet<string>): AxisNode[] {
  return root.children.flatMap(node => node.children.length && expanded.has(node.id)
    ? [...visibleColumns(node, expanded), node] : [node]);
}
